import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import BrandWatermark from '../../components/feature/BrandWatermark';
import { useSEO } from '../../utils/seo';

export default function AboutPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [currentFounderImage, setCurrentFounderImage] = useState(0);

  const founderImages = [
    "https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/5908582197848ed84893561d40c70e8a.jpeg",
    "https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/9e286d52adc1c3ee9a1c790e7c8d3dc1.jpeg",
    "https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/9bfcd2bdda4ca2921f164bd8b2edf56f.jpeg"
  ];

  const [activeYear, setActiveYear] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://gi2merchants.com';

  useSEO({
    title: 'About GI2 Merchants - B2B Printer Consumables Supplier Since 2011',
    description: 'Learn about GI2 Merchants LLP, a B2B supplier of printer consumables since 2011. Started with lamination pouches, now offering a wide range of quality products sourced from trusted global suppliers.',
    keywords: 'about GI2, printer consumables supplier, B2B supplier India, Sanyam Gupta, printer supplies company, wholesale toner supplier, lamination products',
    canonical: `${siteUrl}/about`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About GI2 Merchants LLP',
      description: 'A B2B printing supply partner across India. Quality products sourced from trusted suppliers in China, Japan, Malaysia, Korea, and Taiwan.',
      url: `${siteUrl}/about`,
      mainEntity: {
        '@type': 'Organization',
        name: 'GI2 Merchants LLP',
        foundingDate: '2011',
        founder: {
          '@type': 'Person',
          name: 'Sanyam Gupta',
          jobTitle: 'Founder & Managing Director',
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: '49, Darya Ganj, Behind Yes Bank',
          addressLocality: 'New Delhi',
          addressRegion: 'Delhi',
          postalCode: '110002',
          addressCountry: 'IN',
        },
        areaServed: {
          '@type': 'Country',
          name: 'India',
        },
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tabs = [
    {
      title: 'Who We Serve',
      content: (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-6 bg-white rounded-xl border border-gray-200 hover:border-teal-600 transition-all duration-300 hover:shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-teal-100 mb-4">
              <i className="ri-store-2-line text-2xl text-teal-700"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Retailers</h4>
            <p className="text-sm text-gray-600">Computer shops, stationery stores, and retail outlets needing consistent stock at competitive prices.</p>
          </div>
          <div className="p-6 bg-white rounded-xl border border-gray-200 hover:border-teal-600 transition-all duration-300 hover:shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-teal-100 mb-4">
              <i className="ri-building-line text-2xl text-teal-700"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Wholesalers</h4>
            <p className="text-sm text-gray-600">Bulk buyers and distributors looking for reliable supply chains and volume-based pricing.</p>
          </div>
          <div className="p-6 bg-white rounded-xl border border-gray-200 hover:border-teal-600 transition-all duration-300 hover:shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-teal-100 mb-4">
              <i className="ri-tools-line text-2xl text-teal-700"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Service Centers</h4>
            <p className="text-sm text-gray-600">Printer repair and maintenance centers requiring genuine parts and consumables on demand.</p>
          </div>
        </div>
      ),
    },
    {
      title: 'What We Supply',
      content: (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { icon: 'ri-printer-line', name: 'Laser Toner Cartridges' },
            { icon: 'ri-ink-bottle-line', name: 'Generic Inks' },
            { icon: 'ri-file-copy-line', name: 'Lamination Products' },
            { icon: 'ri-settings-3-line', name: 'Printer Parts' },
            { icon: 'ri-file-text-line', name: 'Photo Papers' },
            { icon: 'ri-palette-line', name: 'Sublimation Inks' },
            { icon: 'ri-droplet-line', name: 'Bulk Inks' },
            { icon: 'ri-printer-cloud-line', name: 'Continuous Ink Systems' },
            { icon: 'ri-layout-grid-line', name: 'Accessories' },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-teal-50 transition-colors duration-200">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white shadow-sm">
                <i className={`${item.icon} text-xl text-teal-700`}></i>
              </div>
              <span className="text-sm font-medium text-gray-800">{item.name}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: 'How We Work',
      content: (
        <div className="space-y-4">
          {[
            { step: '1', title: 'Request Quote', desc: 'Share your requirements via WhatsApp, email, or quote form. Get response within same business day.' },
            { step: '2', title: 'Review Pricing', desc: 'Receive transparent bulk pricing with GST breakdown. MOQ varies by category and product.' },
            { step: '3', title: 'Place Order', desc: 'Confirm order with advance payment. We accept bank transfer, UPI, and other B2B payment methods.' },
            { step: '4', title: 'Fast Dispatch', desc: 'Orders dispatched within 24-48 hours. Pan-India logistics with tracking for all shipments.' },
          ].map((item, idx) => (
            <div key={idx} className="flex gap-4 p-4 bg-white rounded-lg border border-gray-200 hover:border-teal-600 transition-all duration-300">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-700 text-white font-bold flex-shrink-0">
                {item.step}
              </div>
              <div>
                <h4 className="text-base font-bold text-gray-900 mb-1">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: 'Why Partners Stay',
      content: (
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { icon: 'ri-checkbox-circle-line', title: 'Quality First', desc: 'We prioritize quality over quantity. Every product is sourced from reputed suppliers we trust.' },
            { icon: 'ri-global-line', title: 'Global Sourcing', desc: 'Products sourced from trusted suppliers in China, Japan, Malaysia, Korea, and Taiwan.' },
            { icon: 'ri-customer-service-2-line', title: 'Responsive Support', desc: 'Quick response to queries, order updates, and issue resolution. Real people, not bots.' },
            { icon: 'ri-price-tag-3-line', title: 'Fair Pricing', desc: 'Competitive bulk rates with transparent GST invoicing. No hidden charges or surprises.' },
          ].map((item, idx) => (
            <div key={idx} className="flex gap-4 p-5 bg-white rounded-xl border border-gray-200 hover:border-teal-600 transition-all duration-300 hover:shadow-lg">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-teal-100 flex-shrink-0">
                <i className={`${item.icon} text-2xl text-teal-700`}></i>
              </div>
              <div>
                <h4 className="text-base font-bold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      ),
    },
  ];

  const timeline = [
    { year: 2011, title: 'The Beginning', desc: 'Started with quality lamination pouches for local businesses in Delhi. We believed in doing one thing really well.' },
    { year: 2014, title: 'Listening to Partners', desc: 'Customers asked for more products. We listened and gradually expanded our range based on their needs.' },
    { year: 2017, title: 'Building the Catalog', desc: 'Introduced toner cartridges, OPC drums, and printer parts. We only added products we could stand behind.' },
    { year: 2020, title: 'Global Partnerships', desc: 'Established relationships with quality suppliers across Asia. Better products at competitive prices without compromising standards.' },
    { year: 2024, title: 'Serving India', desc: 'Today, we serve retailers, wholesalers, and service centers across multiple states. Our philosophy remains: quality over quantity.' },
  ];

  const officePhotos = [
    { url: 'https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/daacc638c6f8996ad7f640a664decec0.jpeg', caption: 'Office Space' },
    { url: 'https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/498e59f3a16f6df3c3034b86589f2764.jpeg', caption: 'Laminating Pouch' },
    { url: 'https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/15f7f316fc797108d6af30e19c94e476.jpeg', caption: 'Laser Toner Cartridge' },
    { url: 'https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/2f13885fe8e504c676b202ad0025aa4c.jpeg', caption: 'Our Team' },
    { url: 'https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/8e985114f2c83f5d07368383896e0c54.jpeg', caption: 'Our Director' },
    { url: 'https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/137cd450f3be00eb0e5966e76bdb6928.jpeg', caption: 'Toner' },
    { url: 'https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/ba22708fd21fdade8ee9e597c7605ef9.jpeg', caption: 'Our Products' },
    { url: 'https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/ef79dfddcfe4e2938d317fff7f73405d.jpeg', caption: 'Color Laser Printer Toner' },
  ];

  const handleWhatsApp = () => {
    window.open('https://wa.me/919711165605?text=Hi, I want to know more about GI2', '_blank');
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % officePhotos.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + officePhotos.length) % officePhotos.length);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="relative py-12 lg:py-16 bg-gradient-to-br from-teal-800 to-teal-900 text-white overflow-hidden">
        <BrandWatermark variant="dark" position="top-left" size="large" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-64 h-64 border border-white rounded-full animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-48 h-48 border border-white rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6 animate-fade-in-up">
              <h1 className="text-4xl lg:text-5xl font-extrabold leading-tight">
                Your B2B Partner for Printer Consumables
              </h1>
              <p className="text-lg text-teal-100 leading-relaxed">
                Since 2011, we've been supplying quality printer consumables and lamination products to businesses across India. We believe in quality over quantity.
              </p>

              {/* Trust Chips */}
              <div className="flex flex-wrap gap-3 pt-2">
                {[
                  { icon: 'ri-calendar-line', text: 'Since 2011' },
                  { icon: 'ri-global-line', text: 'Global Sourcing' },
                  { icon: 'ri-map-pin-line', text: 'Pan-India Dispatch' },
                  { icon: 'ri-secure-payment-line', text: 'GST: 07AAWFG3117C1ZI' },
                ].map((chip, idx) => (
                  <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                    <i className={`${chip.icon} text-lg`}></i>
                    <span className="text-sm font-medium">{chip.text}</span>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-white text-teal-800 font-bold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  Get Bulk Quote
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
                <button
                  onClick={handleWhatsApp}
                  className="px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  WhatsApp
                </button>
              </div>
            </div>

            {/* Right - Office Photo Collage */}
            <div className="relative hidden lg:block">
              <div className="relative">
                <div className="absolute top-0 right-0 w-72 h-64 rounded-2xl overflow-hidden shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  <img
                    src="https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/f88435600f4551c88b28506de3a2dea4.jpeg"
                    alt="Warehouse"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute bottom-0 left-0 w-72 h-64 rounded-2xl overflow-hidden shadow-2xl transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                  <img
                    src="https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/9a8467590cd123fc9d21018e9f432c88.jpeg"
                    alt="Dispatch"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-56 rounded-2xl overflow-hidden shadow-2xl z-10 hover:scale-110 transition-transform duration-500">
                  <img
                    src="https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/0b7d57302b562c371a76e2490dadb2c8.jpeg"
                    alt="Office"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUR STORY - NEW EXPANDED SECTION */}
      <section className="py-10 lg:py-12 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
                Our Story
              </h2>
              <p className="text-base text-gray-600">
                How we grew from a single product to a trusted B2B partner
              </p>
            </div>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <div className="bg-gray-50 rounded-xl p-6 lg:p-8 border border-gray-200">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-teal-100 flex-shrink-0">
                    <i className="ri-seedling-line text-2xl text-teal-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">The Beginning: 2011</h3>
                    <p className="text-sm">
                      GI2 Merchants started in 2011 with a focused vision to supply high-quality lamination pouches to businesses in Delhi. We wanted to do one thing exceptionally well. For printing shops and offices, lamination pouches are essential, and we made sure every pouch met the quality standards our customers deserved.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 lg:p-8 border border-gray-200">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-teal-100 flex-shrink-0">
                    <i className="ri-customer-service-2-line text-2xl text-teal-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Listening to Our Partners</h3>
                    <p className="text-sm">
                      Our customers started asking for more products like toner cartridges and printer parts. They trusted us and wanted to source more from a reliable supplier. We listened and gradually expanded our range based on what our partners actually needed.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 lg:p-8 border border-gray-200">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-teal-100 flex-shrink-0">
                    <i className="ri-global-line text-2xl text-teal-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Global Sourcing, Local Trust</h3>
                    <p className="text-sm">
                      We established partnerships with reputed suppliers from China, Japan, Malaysia, Korea, and Taiwan. These are manufacturers we've personally vetted and worked with for years. This global network allows us to offer quality products at competitive prices.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-6 lg:p-8 border-2 border-teal-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-teal-600 flex-shrink-0">
                    <i className="ri-heart-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Quality Over Quantity: Our Motto</h3>
                    <p className="text-sm">
                      Our motto has always been <strong className="text-teal-700">"Quality over Quantity."</strong> We'd rather supply fewer products that work reliably than flood the market with inconsistent stock. That trust is earned through consistent quality, order after order. Before adding a new product, we ask: "Is this something we'd use in our own business?" If not, we don't stock it.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 lg:p-8 border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-teal-100 flex-shrink-0">
                    <i className="ri-team-line text-2xl text-teal-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Today: Serving Businesses Across India</h3>
                    <p className="text-sm">
                      Today, GI2 Merchants serves retailers, wholesalers, and service centers across India. Our catalog has grown to include laser toner cartridges, generic inks, OPC drums, photo papers, sublimation inks, and more. Our core values remain unchanged. We still prioritize quality and treat every order with the same care we did in 2011.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK STORY - TABBED SECTION */}
      <section className="py-10 lg:py-12 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              What GI2 Stands For
            </h2>
            <p className="text-base text-gray-600">
              Everything you need to know about partnering with us
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6 -mx-4 px-4 lg:mx-0 lg:px-0">
            <div className="flex lg:flex-wrap lg:justify-center gap-3 overflow-x-auto lg:overflow-x-visible" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
              <style>{`
                .flex::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              {tabs.map((tab, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTab(idx)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer flex-shrink-0 ${
                    activeTab === idx
                      ? 'bg-teal-700 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {tab.title}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-10 min-h-[400px]">
              <div className="animate-fade-in">
                {tabs[activeTab].content}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER SECTION */}
      <section className="py-10 lg:py-12 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <div className="relative grid lg:grid-cols-5 gap-8 items-center bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl p-8 lg:p-12 border border-gray-100 hover:shadow-2xl transition-shadow duration-500">
              <BrandWatermark variant="subtle" position="top-right" size="medium" />
              {/* Left - Founder Photo Gallery */}
              <div className="lg:col-span-2">
                <div className="relative w-full max-w-xs mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-600 to-teal-800 rounded-2xl transform rotate-3"></div>
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                    <img
                      src={founderImages[currentFounderImage]}
                      alt="Founder"
                      className="w-full h-full object-cover object-top"
                    />
                    
                    {/* Navigation Arrows */}
                    <button
                      onClick={() => setCurrentFounderImage((prev) => (prev === 0 ? founderImages.length - 1 : prev - 1))}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white text-gray-800 rounded-full shadow-lg transition-all duration-300 cursor-pointer"
                      aria-label="Previous image"
                    >
                      <i className="ri-arrow-left-s-line text-lg"></i>
                    </button>
                    <button
                      onClick={() => setCurrentFounderImage((prev) => (prev === founderImages.length - 1 ? 0 : prev + 1))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white text-gray-800 rounded-full shadow-lg transition-all duration-300 cursor-pointer"
                      aria-label="Next image"
                    >
                      <i className="ri-arrow-right-s-line text-lg"></i>
                    </button>

                    {/* Image Indicators */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
                      {founderImages.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentFounderImage(idx)}
                          className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                            currentFounderImage === idx
                              ? 'bg-white w-6'
                              : 'bg-white/50 hover:bg-white/75'
                          }`}
                          aria-label={`View image ${idx + 1}`}
                        ></button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Founder Info */}
              <div className="lg:col-span-3 space-y-6">
                <div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                    Sanyam Gupta
                  </h3>
                  <p className="text-base text-teal-700 font-semibold">
                    Founder & Managing Director
                  </p>
                </div>

                <div className="space-y-3">
                  {[
                    { icon: 'ri-time-line', text: 'Started GI2 in 2011 with lamination pouches' },
                    { icon: 'ri-focus-3-line', text: 'Philosophy: Quality over quantity' },
                    { icon: 'ri-global-line', text: 'Sources from trusted suppliers in Asia' },
                    { icon: 'ri-shield-check-line', text: 'Committed to reliable, quality products' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-100 flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-lg text-teal-700`}></i>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-base font-bold text-gray-900 mb-3">Message from the Founder</h4>
                  <p className="text-sm text-gray-600 leading-relaxed italic">
                    "When I started GI2 in 2011, my goal was to supply products I would be proud to use myself. We began with lamination pouches and gradually added more products. The principle has never changed: quality over quantity. We source from reputed suppliers in China, Japan, Malaysia, Korea, and Taiwan, working only with manufacturers who share our commitment to quality. I'd rather supply fewer products that work reliably than chase volume at the cost of our reputation."
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    onClick={handleWhatsApp}
                    className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-lg"></i>
                    Talk to Us
                  </button>
                  <Link
                    to="/contact"
                    className="px-6 py-3 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    Request Pricing
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* JOURNEY TIMELINE */}
      <section className="py-10 lg:py-12 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Our Journey
            </h2>
            <p className="text-base text-gray-600">
              Steady growth since 2011
            </p>
          </div>

          {/* Desktop - Horizontal Timeline */}
          <div className="hidden lg:block max-w-6xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute top-12 left-0 right-0 h-1 bg-gradient-to-r from-teal-600 via-teal-700 to-teal-800"></div>

              {/* Timeline Items */}
              <div className="grid grid-cols-5 gap-4">
                {timeline.map((item, idx) => (
                  <div key={idx} className="relative">
                    {/* Dot */}
                    <div className="absolute top-10 left-1/2 -translate-x-1/2 w-6 h-6 bg-teal-700 rounded-full border-4 border-white shadow-lg z-10"></div>

                    {/* Content */}
                    <div className="pt-24 text-center">
                      <div className="inline-block px-4 py-2 bg-teal-700 text-white font-bold rounded-full mb-3 text-sm">
                        {item.year}
                      </div>
                      <h4 className="text-base font-bold text-gray-900 mb-2">
                        {item.title}
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile - Accordion Timeline */}
          <div className="lg:hidden max-w-2xl mx-auto space-y-3">
            {timeline.map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
                <button
                  onClick={() => setActiveYear(activeYear === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-700 text-white font-bold text-sm flex-shrink-0">
                      {item.year}
                    </div>
                    <h4 className="text-base font-bold text-gray-900 text-left">
                      {item.title}
                    </h4>
                  </div>
                  <i className={`ri-arrow-${activeYear === idx ? 'up' : 'down'}-s-line text-xl text-gray-600`}></i>
                </button>
                {activeYear === idx && (
                  <div className="px-4 pb-4 animate-fade-in">
                    <p className="text-sm text-gray-600 leading-relaxed pl-13">
                      {item.desc}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL SOURCING SECTION */}
      <section className="py-10 lg:py-12 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Quality Products, Trusted Sources
            </h2>
            <p className="text-base text-gray-600 mb-8">
              We source our products from reputed and quality suppliers based in:
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {['China', 'Japan', 'Malaysia', 'Korea', 'Taiwan'].map((country, idx) => (
                <div key={idx} className="px-6 py-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-teal-600 hover:bg-teal-50 transition-all duration-300">
                  <span className="text-base font-semibold text-gray-800">{country}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-6">
              Our philosophy is simple: quality over quantity. We carefully select suppliers who meet our standards.
            </p>
          </div>
        </div>
      </section>

      {/* OFFICE PHOTOS GALLERY */}
      <section className="py-10 lg:py-12 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Inside GI2
            </h2>
            <p className="text-base text-gray-600">
              A glimpse into our operations
            </p>
          </div>

          {/* Carousel */}
          <div className="relative max-w-6xl mx-auto">
            <div className="overflow-hidden rounded-2xl">
              <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
                {officePhotos.map((photo, idx) => (
                  <div key={idx} className="min-w-full px-2">
                    <div className="relative h-96 lg:h-[500px] rounded-xl overflow-hidden shadow-2xl">
                      <img
                        src={photo.url}
                        alt={photo.caption}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                        <p className="text-white text-xl font-bold">{photo.caption}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer z-10"
            >
              <i className="ri-arrow-left-s-line text-2xl text-gray-800"></i>
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer z-10"
            >
              <i className="ri-arrow-right-s-line text-2xl text-gray-800"></i>
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {officePhotos.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    currentSlide === idx ? 'bg-teal-700 w-8' : 'bg-gray-300'
                  }`}
                ></button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-10 lg:py-12 bg-gradient-to-br from-teal-700 to-teal-900 text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl lg:text-4xl font-bold">
              Looking for a reliable B2B supplier?
            </h2>
            <p className="text-lg text-teal-100">
              Quality products, fair pricing, and dependable service since 2011
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Link
                to="/contact"
                className="px-8 py-4 bg-white text-teal-800 font-bold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Get Bulk Quote
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
              <a
                href="tel:+919711165605"
                className="px-8 py-4 bg-teal-600 hover:bg-teal-500 text-white font-bold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                Call Now
              </a>
              <button
                onClick={handleWhatsApp}
                className="px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WhatsApp
              </button>
            </div>
            <p className="text-sm text-teal-200 pt-2">
              B2B only. MOQ may apply based on category.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
